import React, { createContext, useContext, useState, useEffect } from 'react';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);

const sampleNotifications = [
    {
        id: 1,
        title: "Season Stats Updated",
        message: "Season stats have been updated. Please refer to community server for further updates.",
        timestamp: new Date(Date.now() - 1000 * 60 * 5).toISOString(), // 5 minutes ago
        type: "info"
    },
    {
        id: 3,
        title: "NFT Decay System",
        message: "NFTs will gradually decay over time if claims are not made.",
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(), // 2 hours ago
        type: "success"
    }
];

  // Load notifications from sample data only (not from localStorage)
  const loadNotifications = () => {
    try {
      // Always use fresh sample notifications, don't store messages in localStorage
      setNotifications(sampleNotifications);
    } catch (error) {
      console.error('Error loading notifications:', error);
      setNotifications(sampleNotifications);
    }
  };

  // Clear localStorage history
  const clearStorageHistory = () => {
    try {
      localStorage.removeItem('gameNotifications');
      localStorage.removeItem('viewedNotifications');
      console.log('Notification storage history cleared');
    } catch (error) {
      console.error('Error clearing storage history:', error);
    }
  };

  // Force reload notifications with fresh sample data
  const reloadNotifications = () => {
    setNotifications(sampleNotifications);
    // Clear viewed notifications to show badge again
    localStorage.removeItem('viewedNotifications');
    setHasUnreadNotifications(true);
  };

  // Check for unread notifications
  const checkUnreadNotifications = (notificationList = notifications) => {
    try {
      const viewedNotifications = JSON.parse(localStorage.getItem('viewedNotifications') || '[]');
      const unreadExists = notificationList.some(notification => 
        !viewedNotifications.includes(notification.id)
      );
      setHasUnreadNotifications(unreadExists);
    } catch (error) {
      console.error('Error checking unread notifications:', error);
      setHasUnreadNotifications(false);
    }
  };

  // Mark notifications as viewed
  const markNotificationsAsViewed = () => {
    try {
      const notificationIds = notifications.map(n => n.id);
      localStorage.setItem('viewedNotifications', JSON.stringify(notificationIds));
      setHasUnreadNotifications(false);
    } catch (error) {
      console.error('Error marking notifications as viewed:', error);
    }
  };

  // Add new notification (in-memory only, not persisted)
  const addNotification = (notification) => {
    const newNotification = {
      ...notification,
      id: Date.now(), // Simple ID generation
      timestamp: new Date().toISOString()
    };
    
    const updatedNotifications = [newNotification, ...notifications];
    setNotifications(updatedNotifications);
    
    // Only update unread status, don't store notifications
    checkUnreadNotifications(updatedNotifications);
  };

  // Remove notification (in-memory only)
  const removeNotification = (notificationId) => {
    const updatedNotifications = notifications.filter(n => n.id !== notificationId);
    setNotifications(updatedNotifications);
    
    // Only update unread status
    checkUnreadNotifications(updatedNotifications);
  };

  // Clear all notifications (in-memory only)
  const clearAllNotifications = () => {
    setNotifications([]);
    // Only clear viewed status, notifications come from sample data
    localStorage.removeItem('viewedNotifications');
    setHasUnreadNotifications(false);
  };

  // Format time ago
  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInSeconds = Math.floor((now - time) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  // Initialize notifications on mount
  useEffect(() => {
    // Clear any existing localStorage history first
    // clearStorageHistory();
    // Load fresh notifications
    loadNotifications();
  }, []);

  // Check unread status when notifications change
  useEffect(() => {
    if (notifications.length > 0) {
      checkUnreadNotifications();
    }
  }, [notifications]);

  const value = {
    notifications,
    hasUnreadNotifications,
    markNotificationsAsViewed,
    addNotification,
    removeNotification,
    clearAllNotifications,
    formatTimeAgo,
    loadNotifications,
    reloadNotifications,
    clearStorageHistory
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
